
import { Vue, Component, Prop } from "vue-property-decorator";
import GET_CONTRACTOR_ACTIVITIES from "@/apollo/projectActivities/GetContractorActivities.gql";
import { User, Company } from "@/generated/graphql";
import { cloneDeep, isEqual } from "lodash-es";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import RouteNames from "@/entities/RouteNames";
import ClientFilters from "@/components/companyProjects/ClientMasterviewFilters.vue";
import ProjectsFiltersQuery from "@/entities/filters/ProjectsFiltersQuery";
import projectsFiltersTemplate from "@/data/filtersTemplates/projectsFiltersTemplate";
import CompletedVisitCard from "@/components/projects/activities/CompletedVisitCard.vue";
import moment from "moment";

@Component({
  components: {
    LoadingOverlay,
    CardPagination,
    CompletedVisitCard,
    EmptyListPlaceholder,
    ClientFilters,
    EmptyListImageProjects,
  },
  apollo: {
    activities: {
      query: GET_CONTRACTOR_ACTIVITIES,
      variables() {
        return this.activitiesArgs;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.project_activity.length;
        this.totalCount = data.total.aggregate.count;

        // Sort in reverse chronological order (newest first)
        const sortedActivities = [...data.project_activity].sort((a, b) => {
          const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
          const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
          return dateB - dateA;
        });

        return sortedActivities;
      },
    },
  },
})
export default class CompletedVisits extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;

  activities: any[] | null = null;
  paginationConfig: PaginationConfig = { ...paginationConfig, limit: 50 };
  routeNames = RouteNames;
  totalCount: number = 0;

  get currentUserId(): string {
    return this.currentUser?.id;
  }

  get currentUserRole(): string {
    return this.currentUser?.role;
  }

  get activitiesArgs(): Object {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;

    // Build filter conditions
    const filterConditions: any[] = [
      { deleted: { _eq: false } },
      { completed: { _eq: true } },
    ];

    // Add company filter for admins/managers
    if (this.company?.id) {
      filterConditions.push({
        project: {
          company_id: { _eq: this.company.id },
        },
      });
    }

    // Apply additional filters from the filter component
    const { title, managers, contractors, location, createdAt } = this.projectFilters;

    if (managers.length) {
      filterConditions.push({
        project: {
          manager: {
            id: { _in: managers },
          },
        },
      });
    }

    if (contractors.length) {
      filterConditions.push({
        contractor: {
          id: {
            _in: contractors,
          },
        },
      });
    }

    if (title) {
      filterConditions.push({
        project: {
          client_name: { _ilike: `%${title}%` },
        },
      });
    }

    if (location) {
      filterConditions.push({
        location: { _ilike: `%${location}%` }
      });
    }

    if (createdAt) {
      const startDate = moment(createdAt)
        .utc()
        .startOf("day")
        .toISOString();
      const endDate = moment(createdAt)
        .utc()
        .endOf("day")
        .toISOString();

      filterConditions.push({
        _and: [
          { start_date: { _gte: startDate } },
          { start_date: { _lte: endDate } },
        ],
      });
    }

    return {
      offset: nextOffset,
      limit,
      where: {
        _and: filterConditions,
      },
    };
  }

  get loading(): boolean {
    return this.$apollo.queries.activities.loading;
  }

  get empty(): boolean {
    if (!this.activities) {
      return false;
    }
    return this.activities?.length === 0;
  }

  projectFilters: ProjectsFiltersQuery = cloneDeep(projectsFiltersTemplate);

  updateFilters(filters: ProjectsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.projectFilters);

    if (dataEqual) {
      return;
    }
    this.projectFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.projectFilters, projectsFiltersTemplate)) {
      return;
    }

    this.projectFilters = cloneDeep(projectsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig, limit: 50 };
  }

  get emptyListMessage(): string {
    return "You currently do not have any completed visits";
  }
}
